// The named commands the bridge may ask of the page, each a function of the
// APIs and its arguments, returning exactly what the bridge's tools built
// from a page expression until 2026-10-21 (`ai/server/src/tools.ts`), so the
// coach reads the same shapes: the judged moves under the user's mistake
// rules, the board state with its limits and the scan's lines in SAN, the
// engine's lines under an analysis hold, the opening book of the open game.
// Nothing here evaluates text; a production build answers these and only
// these. Pure where it can be: the APIs come in through `CommandContext`,
// so Node's test runner drives them with fakes.
import type { Analysis } from '@chess/api-analysis';
import type { Chess, MoveInput } from '@chess/api-chess';
import type { Engine, EngineLine } from '@chess/api-engine';
import { DEFAULT_MISTAKE_RULES, type Fen, type GameStore, type GameSummary, type Line, type MistakeRules, type MistakeRulesSource, type Move, type MoveJudgement, type Scan, type Score, type Uci } from '@chess/api-model';
import type { GameBook, Review, ReviewState } from '@chess/api-review';
import type { Opening, OpeningBook, OpeningContinuation, Openings } from '@chess/api-openings';

export const GAMES_LIMIT_DEFAULT = 30;
export const GAMES_LIMIT_MAX = 500;
export const DEPTH_DEFAULT = 16;
export const DEPTH_MAX = 22;
export const MULTIPV_DEFAULT = 3;
export const MULTIPV_MAX = 5;
/** How long `game_open` waits for the review to be ready for the game. */
export const OPEN_WAIT_MS = 10_000;

/** What the commands need of the page. */
export interface CommandContext {
  /** The API registered under `name`, or undefined. */
  api(name: string): unknown;
  /** The page's path, and a way to change it without a reload (`game_open`). */
  pathname(): string;
  navigate(path: string): void;
  timers?: { setTimeout(fn: () => void, ms: number): unknown; clearTimeout(handle: unknown): void };
}

export type CommandArgs = Record<string, unknown>;
export type Command = (args: CommandArgs, context: CommandContext) => Promise<unknown>;

// ---- The helpers the bridge's prelude had, in TypeScript ----

/** A score as "+0.35" or "#3" (mate in 3 for the side to move, "#-2" mated in 2). */
export function scoreText(s: Score): string {
  if (s.mate !== undefined) return `#${s.mate}`;
  const cp = s.cp ?? 0;
  return `${cp >= 0 ? '+' : ''}${(cp / 100).toFixed(2)}`;
}

export type ScoreOut = Score & { text: string };

/** The score with its text; undefined stays undefined. */
export function withText(s: Score | undefined): ScoreOut | undefined {
  return s ? { ...s, text: scoreText(s) } : undefined;
}

/** Centipawns for the side to move; a mate counts as nearly won or lost. */
export function cpOf(s: Score): number {
  if (s.mate !== undefined) return s.mate === 0 ? -10000 : s.mate > 0 ? 10000 - s.mate : -10000 - s.mate;
  return s.cp ?? 0;
}

/** The same score from the other side. */
export function negate(s: Score): Score {
  return s.mate !== undefined ? { mate: s.mate === 0 ? 0 : -s.mate } : { cp: -(s.cp ?? 0) };
}

export function sideOf(fen: string | undefined): 'white' | 'black' {
  return fen && fen.split(' ')[1] === 'b' ? 'black' : 'white';
}

/** A UCI move as the `chess` API's MoveInput; undefined for anything else (SAN stays SAN). */
export function moveInputOf(move: string): MoveInput | undefined {
  const m = /^([a-h][1-8])([a-h][1-8])([qrbn])?$/i.exec(move.trim());
  if (!m) return undefined;
  const input: MoveInput = { from: m[1].toLowerCase() as MoveInput['from'], to: m[2].toLowerCase() as MoveInput['to'] };
  if (m[3]) input.promotion = m[3].toLowerCase() as NonNullable<MoveInput['promotion']>;
  return input;
}

const uciInput = (u: Uci): MoveInput => moveInputOf(u) ?? ({ from: u.slice(0, 2), to: u.slice(2, 4) } as MoveInput);

/** The move's SAN from the position, or the UCI itself when it cannot be played there. */
export function sanOf(chess: Chess | undefined, fen: Fen | undefined, uci: Uci): string {
  if (!chess || !fen || !uci) return uci;
  const r = chess.play(fen, uciInput(uci));
  return r ? r.move.san : uci;
}

/** A principal variation in SAN, cut at the first move that cannot be played. */
export function sanLine(chess: Chess | undefined, fen: Fen | undefined, pv: Uci[]): string[] {
  if (!chess || !fen) return pv;
  const out: string[] = [];
  let f = fen;
  for (const u of pv) {
    const r = chess.play(f, uciInput(u));
    if (!r) break;
    out.push(r.move.san);
    f = r.fen;
  }
  return out;
}

export interface LineOut {
  uci: Uci;
  san: string;
  score: ScoreOut | undefined;
  pv: string[];
}

/** One engine or scan line as the coach reads it: first move in UCI and SAN, the score with text, the variation in SAN. */
export function lineOut(chess: Chess | undefined, fen: Fen | undefined, l: { uci?: Uci; pv: Uci[]; score: Score }): LineOut {
  const uci = l.uci ?? l.pv[0];
  return { uci, san: sanOf(chess, fen, uci), score: withText(l.score), pv: sanLine(chess, fen, l.pv) };
}

/** "12. Nf3" or "12... Nf6". */
export const moveLabel = (ply: number, san: string): string => `${Math.ceil(ply / 2)}${ply % 2 ? '. ' : '... '}${san}`;

/** The user's mistake rules, or the defaults when the API is absent. */
export function rulesOf(context: CommandContext): MistakeRules {
  const source = context.api('mistakeRules') as MistakeRulesSource | undefined;
  const v = source && typeof source.get === 'function' ? source.get() : undefined;
  return { ...DEFAULT_MISTAKE_RULES, ...(v ?? {}) };
}

const judge = (delta: number, isBest: boolean): MoveJudgement['judgement'] => (isBest || delta <= 0 ? 'best' : delta >= 100 ? 'blunder' : delta >= 50 ? 'mistake' : delta >= 10 ? 'inaccuracy' : 'good');

/** The bridge's miss rule, the same as `isMissOf` in @chess/api-review. */
export function isMiss(lines: Line[] | undefined, played: Uci | undefined, gapCp: number): boolean {
  const [best, second] = lines ?? [];
  if (!best || !second || !played || played === best.uci) return false;
  if (best.score.mate !== undefined) {
    if (best.score.mate <= 0) return false;
    return !(second.score.mate !== undefined && second.score.mate > 0);
  }
  const s = best.score.cp ?? 0;
  if (s < 0) return false;
  if (second.score.mate !== undefined) return second.score.mate < 0;
  return s - (second.score.cp ?? 0) >= gapCp;
}

export interface JudgedMove {
  ply: number;
  move: string;
  by: 'white' | 'black';
  judgement: MoveJudgement['judgement'];
  kind: 'miss' | 'blunder' | undefined;
  /** Pawns given up against the best line. */
  cost: number;
  best: string;
  bestUci: Uci;
  scoreBefore: ScoreOut | undefined;
  scoreAfter: ScoreOut | undefined;
}

/** Every judged move of a scan: the stored judgement, or one worked out from the two positions around the move, with the app's kind under `rules`. */
export function judgedMoves(chess: Chess | undefined, scan: Scan, moves: Pick<Move, 'uci' | 'san'>[], rules: MistakeRules): JudgedMove[] {
  const byPly = new Map(scan.positions.map((p) => [p.ply, p]));
  const out: JudgedMove[] = [];
  for (let ply = 1; ply <= moves.length; ply++) {
    const before = byPly.get(ply - 1);
    const after = byPly.get(ply);
    const move = moves[ply - 1];
    if (!before || !before.lines[0] || !move) continue;
    let j = scan.moves.find((m) => m.ply === ply);
    if (!j) {
      if (!after) continue;
      const best = before.lines[0];
      const isBest = best.uci === move.uci;
      const delta = isBest ? 0 : Math.max(0, cpOf(best.score) - cpOf(negate(after.score)));
      j = { ply, uci: move.uci, san: move.san, best: best.uci, delta, judgement: judge(delta, isBest) };
    }
    const afterCp = after ? cpOf(negate(after.score)) : undefined;
    let kind: JudgedMove['kind'];
    if (isMiss(before.lines, move.uci, rules.missGapPawns * 100) && afterCp !== undefined && afterCp >= rules.missHoldPawns * 100) kind = 'miss';
    else if (j.uci !== j.best && j.delta >= rules.blunderPawns * 100) kind = 'blunder';
    out.push({
      ply,
      move: moveLabel(ply, move.san),
      by: ply % 2 ? 'white' : 'black',
      judgement: j.judgement,
      kind,
      cost: Math.round(j.delta) / 100,
      best: sanOf(chess, before.fen, j.best),
      bestUci: j.best,
      scoreBefore: withText(before.score),
      scoreAfter: after ? withText(after.score) : undefined,
    });
  }
  return out;
}

/** The review address of a game, as the app builds it (`reviewPath` in @chess/api-games). */
export function reviewPathOf(g: Pick<GameSummary, 'id' | 'owner' | 'source' | 'playedAt'>): string {
  const website = g.source.replace(/[^a-z0-9]/gi, '').toLowerCase();
  const date = g.playedAt.slice(0, 10) || 'undated';
  return `/review/${website}/${encodeURIComponent(g.owner)}/${date}/${encodeURIComponent(g.id)}`;
}

/** The game's moves and positions: the review's when it has this game open, else parsed from the stored PGN. */
export async function gameOf(context: CommandContext, store: GameStore, chess: Chess, id: string): Promise<{ id: string; moves: Move[]; positions: Fen[]; result: string }> {
  const review = context.api('review') as Review | undefined;
  const s = review && typeof review.state === 'function' ? review.state() : undefined;
  if (s?.game && s.game.id === id) return s.game;
  const rec = await store.record(id);
  if (!rec) throw new Error(`no game ${id} in storage`);
  const p = chess.parsePgn(rec.pgn);
  return { id, moves: p.moves, positions: p.positions, result: p.result };
}

/** The review's state as a coach needs it: what the board shows, the scan's reading there, the limits. */
export function boardSummary(context: CommandContext): Record<string, unknown> {
  const review = need<Review>(context, 'review');
  const chess = context.api('chess') as Chess | undefined;
  const s = review.state();
  const game = s.game;
  const base = { status: s.status, gameId: s.gameId, error: s.error };
  if (!game) return base;
  const line = s.exploring;
  const head = line ? line.fromPly + line.moves.length : s.ply;
  const view = s.browse !== undefined ? s.browse : head;
  const posAt = (p: number) => (line && p > line.fromPly ? line.positions[p - line.fromPly] : game.positions[p]);
  const moveAt = (p: number) => (p <= 0 ? undefined : line && p > line.fromPly ? line.moves[p - line.fromPly - 1] : game.moves[p - 1]);
  const fen = posAt(view);
  const last = moveAt(view);
  const onGame = !line || view <= line.fromPly;
  const pos = onGame && s.scan ? s.scan.position(view) : undefined;
  const book = s.book as GameBook | undefined;
  return {
    ...base,
    ply: s.ply,
    headPly: head,
    viewPly: view,
    lookingBack: s.browse !== undefined,
    fen,
    sideToMove: sideOf(fen),
    lastMove: last ? { san: last.san, uci: last.uci, label: moveLabel(view, last.san) } : undefined,
    moveCount: game.moves.length,
    result: game.result,
    reader: s.reader,
    line: line ? { fromPly: line.fromPly, moves: line.moves.map((m) => m.san), fen: line.fen } : undefined,
    theory: s.theory ? { active: s.theory.active, moves: s.theory.moves.map((m) => ({ san: m.san, opening: m.opening.name, eco: m.opening.eco })) } : undefined,
    opening: book?.reading ? book.reading.opening : undefined,
    bookStatus: s.bookStatus,
    scan: s.scan ? { complete: s.scan.complete, depth: s.scan.depth, ...s.scan.progress } : undefined,
    scanning: s.scanning,
    score: pos ? withText(pos.score) : undefined,
    best: pos && pos.lines[0] ? lineOut(chess, pos.fen, pos.lines[0]) : undefined,
    lines: pos ? pos.lines.map((l) => lineOut(chess, pos.fen, l)) : undefined,
    stages: s.stages ? { openingEnd: s.stages.openingEnd, middlegameEnd: s.stages.middlegameEnd, endgameStart: s.stages.endgameStart } : undefined,
    limits: { first: 0, last: game.moves.length, canStepBackward: view > 0, canStepForward: !line && s.browse === undefined && s.ply < game.moves.length, canPlay: s.browse === undefined },
  };
}

/** `depth` and `multiPv` as the engine command uses them: the defaults when absent, never above the caps. */
export function engineOptions(args: { depth?: unknown; multiPv?: unknown }): { depth: number; multiPv: number } {
  const clamp = (v: unknown, fallback: number, max: number) => Math.max(1, Math.min(max, Math.trunc(typeof v === 'number' && Number.isFinite(v) ? v : fallback)));
  return { depth: clamp(args.depth, DEPTH_DEFAULT, DEPTH_MAX), multiPv: clamp(args.multiPv, MULTIPV_DEFAULT, MULTIPV_MAX) };
}

// ---- Arguments ----

function str(args: CommandArgs, name: string, min = 1): string {
  const v = args[name];
  if (typeof v !== 'string' || v.trim().length < min) throw new Error(`argument "${name}" must be a string${min > 1 ? ` of at least ${min} characters` : ''}`);
  return v.trim();
}

function optStr(args: CommandArgs, name: string): string | undefined {
  const v = args[name];
  if (v === undefined || v === null) return undefined;
  if (typeof v !== 'string') throw new Error(`argument "${name}" must be a string`);
  return v.trim() || undefined;
}

function int(args: CommandArgs, name: string, min: number, max = Number.POSITIVE_INFINITY): number {
  const v = args[name];
  if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max) throw new Error(`argument "${name}" must be a whole number from ${min}${Number.isFinite(max) ? ` to ${max}` : ''}`);
  return v;
}

function optInt(args: CommandArgs, name: string, min: number, max?: number): number | undefined {
  const v = args[name];
  return v === undefined || v === null ? undefined : int(args, name, min, max);
}

/** The API, or an error naming it. */
function need<T>(context: CommandContext, name: string): T {
  const a = context.api(name);
  if (!a) throw new Error(`the page has no '${name}' API (plugin not loaded, or not this app)`);
  return a as T;
}

// ---- The commands ----

const games_list: Command = async (args, context) => {
  const limit = optInt(args, 'limit', 1, GAMES_LIMIT_MAX) ?? GAMES_LIMIT_DEFAULT;
  const store = need<GameStore>(context, 'store');
  if (typeof store.ready === 'function') await store.ready();
  const st = store.getState();
  return {
    status: st.status,
    period: st.period ? { from: new Date(st.period.from).toISOString(), to: new Date(st.period.to).toISOString(), games: st.period.games, scanned: st.period.scanned } : undefined,
    total: st.games.length,
    games: st.games.slice(0, limit).map(({ summary: s, facts: f }) => ({
      id: s.id,
      playedAt: s.playedAt,
      white: s.white,
      black: s.black,
      result: s.result,
      timeControl: s.timeControl,
      source: s.source,
      url: s.url,
      colour: f?.colour,
      outcome: f?.outcome,
      opponent: f?.opponent,
      accuracy: f?.accuracy,
      scanned: !!f?.scanned,
      opening: f ? (f.variation ?? f.opening) : undefined,
      reviewPath: f?.reviewPath || reviewPathOf(s),
    })),
  };
};

const game_facts: Command = async (args, context) => {
  const id = str(args, 'id');
  const store = need<GameStore>(context, 'store');
  const chess = need<Chess>(context, 'chess');
  const g = store.game(id);
  if (!g) throw new Error(`no game ${id} in the store (it holds the last 30 days; see games_list)`);
  let scan = await store.scan(id);
  if (!scan) {
    const analysis = context.api('analysis') as Analysis | undefined;
    if (analysis) scan = await analysis.get(id);
  }
  const game = await gameOf(context, store, chess, id);
  return {
    summary: g.summary,
    facts: g.facts,
    reviewPath: g.facts?.reviewPath || reviewPathOf(g.summary),
    moves: game.moves.map((m, i) => (i % 2 ? '' : `${Math.ceil((i + 1) / 2)}. `) + m.san).join(' '),
    moveCount: game.moves.length,
    scan: scan ? { complete: scan.complete, depth: scan.depth, engine: scan.engine, positions: scan.positions.length, total: scan.total } : undefined,
    judged: scan ? judgedMoves(chess, scan, game.moves, rulesOf(context)) : [],
  };
};

const game_open: Command = async (args, context) => {
  const id = str(args, 'id');
  const ply = optInt(args, 'ply', 0);
  const store = need<GameStore>(context, 'store');
  const review = need<Review>(context, 'review');
  const g = store.game(id);
  if (!g) throw new Error(`no game ${id} in the store (see games_list)`);
  const path = g.facts?.reviewPath || reviewPathOf(g.summary);
  const s0 = review.state();
  const same = s0.gameId === id && s0.status === 'ready' && context.pathname() === path;
  if (same) {
    if (ply !== undefined) review.go(ply);
  } else {
    context.navigate(path + (ply !== undefined && ply > 0 ? `#ply=${ply}` : ''));
  }
  const timers = context.timers ?? { setTimeout: (fn: () => void, ms: number) => setTimeout(fn, ms), clearTimeout: (h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>) };
  await new Promise<void>((resolve, reject) => {
    let un: () => void = () => undefined;
    let timer: unknown;
    const check = (s: ReviewState) => {
      if (s.gameId !== id) return false;
      if (s.status === 'ready') {
        resolve();
        return true;
      }
      if (s.status === 'missing' || s.status === 'failed') {
        reject(new Error(`the review could not open ${id}: ${s.status}${s.error ? ` (${s.error})` : ''}`));
        return true;
      }
      return false;
    };
    if (check(review.state())) return;
    un = review.subscribe((s) => {
      if (check(s)) {
        un();
        timers.clearTimeout(timer);
      }
    });
    timer = timers.setTimeout(() => {
      un();
      reject(new Error(`the review did not open game ${id} within ${OPEN_WAIT_MS} ms (the page is at ${context.pathname()})`));
    }, OPEN_WAIT_MS);
  });
  if (ply !== undefined) {
    const s = review.state();
    const head = s.exploring ? s.exploring.fromPly + s.exploring.moves.length : s.ply;
    if (head !== ply || s.browse !== undefined) review.go(ply);
  }
  return boardSummary(context);
};

const board_state: Command = async (_args, context) => boardSummary(context);

const board_go: Command = async (args, context) => {
  const ply = int(args, 'ply', 0);
  need<Review>(context, 'review').go(ply);
  return boardSummary(context);
};

const board_play: Command = async (args, context) => {
  const text = str(args, 'move', 2);
  const review = need<Review>(context, 'review');
  const before = review.state();
  if (!before.game) throw new Error('no game is open in the review');
  if (before.browse !== undefined) throw new Error(`the board is looking back at ply ${before.browse}; nothing can be played until board_go or board_return`);
  const move = moveInputOf(text) ?? text;
  if (!review.play(move)) throw new Error(`illegal move ${text} in ${boardSummary(context).fen || 'the position'}`);
  return boardSummary(context);
};

const board_return: Command = async (_args, context) => {
  const review = need<Review>(context, 'review');
  if (review.state().browse !== undefined) review.browse(undefined);
  review.returnToGame();
  return boardSummary(context);
};

const position_at: Command = async (args, context) => {
  const id = str(args, 'id');
  const ply = int(args, 'ply', 0);
  const store = need<GameStore>(context, 'store');
  const chess = need<Chess>(context, 'chess');
  const game = await gameOf(context, store, chess, id);
  if (ply > game.moves.length) throw new Error(`ply ${ply} is past the end: the game has ${game.moves.length} half-moves`);
  const fen = game.positions[ply];
  const scan = await store.scan(id);
  const pos = scan ? scan.positions.find((p) => p.ply === ply) : undefined;
  const label = (p: number, m: Move | undefined) => (m ? { san: m.san, uci: m.uci, label: moveLabel(p, m.san) } : undefined);
  return {
    id,
    ply,
    fen,
    sideToMove: sideOf(fen),
    lastMove: label(ply, game.moves[ply - 1]),
    nextMove: label(ply + 1, game.moves[ply]),
    scanned: !!pos,
    depth: pos ? pos.depth : undefined,
    score: pos ? withText(pos.score) : undefined,
    lines: pos ? pos.lines.map((l) => lineOut(chess, fen, l)) : [],
  };
};

const engine_lines: Command = async (args, context) => {
  const fen = str(args, 'fen', 10);
  const { depth, multiPv } = engineOptions(args);
  const engine = need<Engine>(context, 'engine');
  const chess = need<Chess>(context, 'chess');
  if (!chess.validateFen(fen)) throw new Error(`not a position the engine can search: ${fen}`);
  const analysis = context.api('analysis') as Analysis | undefined;
  const release = analysis && typeof analysis.hold === 'function' ? analysis.hold() : undefined;
  try {
    const u = await engine.evaluate(fen, { depth, multiPv });
    return {
      fen,
      sideToMove: sideOf(fen),
      depth: u.depth,
      nodes: u.nodes,
      timeMs: u.timeMs,
      lines: [...u.lines].sort((a, b) => a.rank - b.rank).map((l: EngineLine) => ({ rank: l.rank, depth: l.depth, ...lineOut(chess, fen, l) })),
    };
  } finally {
    if (release) release();
  }
};

const opening_lookup: Command = async (args, context) => {
  const fen = str(args, 'fen', 10);
  const firstMove = optStr(args, 'firstMove');
  const chess = context.api('chess') as Chess | undefined;
  if (chess && !chess.validateFen(fen)) throw new Error(`not a valid position: ${fen}`);
  const review = context.api('review') as Review | undefined;
  const s = review && typeof review.state === 'function' ? review.state() : undefined;
  let book: GameBook | OpeningBook | undefined = s?.book;
  let source = 'the open game';
  if (!book) {
    const first = firstMove || s?.game?.moves[0]?.san;
    if (!first) throw new Error('no opening book is loaded: open a game in the review (the review loads the book by the game\'s first move) or pass firstMove, e.g. "e4" or "d4"');
    book = await need<Openings>(context, 'openings').load(first);
    source = `the book for games opening ${first}`;
  }
  const atStart = chess ? fen === chess.startFen : fen.startsWith('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w');
  const moves: OpeningContinuation[] = 'movesFrom' in book ? book.movesFrom(fen, atStart) : book.continuations(fen);
  const opening: Opening | undefined = book.lookup(fen);
  return { fen, opening, known: book.knows(fen), continuations: moves.map((c) => ({ san: c.san, opening: c.opening.name, eco: c.opening.eco, distance: c.distance })), source };
};

export const COMMANDS: Readonly<Record<string, Command>> = Object.freeze({
  games_list,
  game_facts,
  game_open,
  board_state,
  board_go,
  board_play,
  board_return,
  position_at,
  engine_lines,
  opening_lookup,
});

export const COMMAND_NAMES: readonly string[] = Object.freeze(Object.keys(COMMANDS));

/** Runs the named command; an unknown name is an error that lists the known ones. */
export async function runCommand(name: string, args: CommandArgs, context: CommandContext): Promise<unknown> {
  const command = Object.prototype.hasOwnProperty.call(COMMANDS, name) ? COMMANDS[name] : undefined;
  if (!command) throw new Error(`unknown command "${name}"; the commands are ${COMMAND_NAMES.join(', ')}`);
  return command(args, context);
}
